import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Upload, Key, X, ExternalLink, Copy, Mail } from "lucide-react";

const FONT_OPTIONS = [
  "Manrope",
  "Archivo",
  "Inter",
  "DM Sans",
  "Montserrat",
  "Open Sans",
  "Roboto",
];

const emptyForm = {
  name: "",
  city: "",
  logo_url: "",
  primary_color: "#1B3A4B",
  secondary_color: "#C9A66B",
  background_color: "#F4EFE6",
  text_color: "#1A1C20",
  font_family: "Manrope",
  active_product_ids: [],
  featured_product_id: "",
  commission_pct: 10,
  contact_email: "",
  coupon_code: "",
  coupon_discount_label: "",
};

export default function HotelsManager() {
  const [hotels, setHotels] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // {hotel, email, password}
  const [couponHotel, setCouponHotel] = useState(null);
  const [couponFile, setCouponFile] = useState(null);
  const [couponStats, setCouponStats] = useState(null);

  const load = async () => {
    try {
      const [h, p] = await Promise.all([api.get("/admin/hotels"), api.get("/admin/products")]);
      setHotels(h.data);
      setProducts(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyForm); setEditing(null); setShowForm(false); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        contact_email: form.contact_email || null,
        featured_product_id: form.featured_product_id || null,
        commission_pct: Number(form.commission_pct),
      };
      if (editing) {
        await api.put(`/admin/hotels/${editing}`, payload);
        toast.success("Hotel aggiornato");
      } else {
        await api.post("/admin/hotels", payload);
        toast.success("Hotel creato");
      }
      reset();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const edit = (h) => {
    setForm({
      name: h.name,
      city: h.city,
      logo_url: h.logo_url || "",
      primary_color: h.primary_color || "#1B3A4B",
      secondary_color: h.secondary_color || "#C9A66B",
      background_color: h.background_color || "#F4EFE6",
      text_color: h.text_color || "#1A1C20",
      font_family: h.font_family || "Manrope",
      active_product_ids: h.active_product_ids || [],
      featured_product_id: h.featured_product_id || "",
      commission_pct: h.commission_pct ?? 10,
      contact_email: h.contact_email || "",
      coupon_code: h.coupon_code || "",
      coupon_discount_label: h.coupon_discount_label || "",
    });
    setEditing(h.id);
    setShowForm(true);
  };

  const del = async (id) => {
    if (!confirm("Eliminare questo hotel e tutte le credenziali associate?")) return;
    try {
      await api.delete(`/admin/hotels/${id}`);
      toast.success("Hotel eliminato");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const genCreds = async (hotel) => {
    const email = prompt("Email per il portale hotel:", hotel.portal_user_email || `staff@${hotel.name.toLowerCase().replace(/\s+/g, "")}.com`);
    if (!email) return;
    try {
      const { data } = await api.post(`/admin/hotels/${hotel.id}/credentials`, { email });
      setCredsModal({ hotel, email: data.email, password: data.password });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCoupons = async (hotel) => {
    setCouponHotel(hotel);
    setCouponFile(null);
    try {
      const { data } = await api.get(`/admin/hotels/${hotel.id}/coupons`);
      const avail = data.filter((c) => c.status === "available").length;
      const sent = data.filter((c) => c.status === "sent").length;
      const matched = data.filter((c) => c.status === "matched").length;
      setCouponStats({ total: data.length, available: avail, sent, matched, items: data.slice(0, 20) });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCoupons = async () => {
    if (!couponFile) return;
    const fd = new FormData();
    fd.append("file", couponFile);
    try {
      const { data } = await api.post(`/admin/hotels/${couponHotel.id}/coupons/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Caricati ${data.inserted} coupon (${data.duplicates} duplicati)`);
      await openCoupons(couponHotel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato"); };

  const sendCredsEmail = async () => {
    if (!credsModal) return;
    const to = prompt(
      "Invia le credenziali via email a:",
      credsModal.hotel.contact_email || credsModal.email
    );
    if (!to) return;
    try {
      const { data } = await api.post(`/admin/hotels/${credsModal.hotel.id}/send-credentials`, {
        to_email: to,
        portal_email: credsModal.email,
        portal_password: credsModal.password,
      });
      if (data.mocked) {
        toast.warning("SMTP non configurato — email simulata (vai a tab Email SMTP)");
      } else if (data.sent) {
        toast.success(`Email inviata a ${to}`);
      } else {
        toast.error(data.error || "Errore invio email");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleProduct = (pid) => {
    setForm((f) => ({
      ...f,
      active_product_ids: f.active_product_ids.includes(pid)
        ? f.active_product_ids.filter((x) => x !== pid)
        : [...f.active_product_ids, pid],
    }));
  };

  return (
    <div className="space-y-8" data-testid="hotels-manager">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="mgx-display text-4xl text-[#003D5C]">Hotel</h2>
          <p className="text-sm text-[#6B7280] mt-2">Anagrafica hotel, branding, prodotti attivi e credenziali portale.</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} className="mgx-btn-primary inline-flex items-center gap-2" data-testid="add-hotel-btn">
          <Plus size={14} /> Nuovo hotel
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mgx-card grid md:grid-cols-2 gap-6" data-testid="hotel-form">
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="mgx-label">{editing ? "Modifica hotel" : "Nuovo hotel"}</div>
            <button type="button" onClick={reset} className="text-[#6B7280] hover:text-[#003D5C]"><X size={18} /></button>
          </div>

          <div>
            <label className="mgx-label">Nome hotel</label>
            <input required className="mgx-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hotel-name-input" />
          </div>
          <div>
            <label className="mgx-label">Città</label>
            <input required className="mgx-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="hotel-city-input" />
          </div>
          <div className="md:col-span-2">
            <label className="mgx-label">URL Logo hotel</label>
            <input className="mgx-input" placeholder="https://..." value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="hotel-logo-input" />
          </div>
          <div>
            <label className="mgx-label">Colore primario</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-12 h-10 border border-[#E5E7EB] cursor-pointer" data-testid="hotel-primary-color" />
              <input className="mgx-input flex-1" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">Titoli e bottoni principali</div>
          </div>
          <div>
            <label className="mgx-label">Colore accento</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="w-12 h-10 border border-[#E5E7EB] cursor-pointer" data-testid="hotel-secondary-color" />
              <input className="mgx-input flex-1" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} />
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">Dettagli, hover, icone</div>
          </div>
          <div>
            <label className="mgx-label">Colore sfondo</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={form.background_color} onChange={(e) => setForm({ ...form, background_color: e.target.value })} className="w-12 h-10 border border-[#E5E7EB] cursor-pointer" data-testid="hotel-background-color" />
              <input className="mgx-input flex-1" value={form.background_color} onChange={(e) => setForm({ ...form, background_color: e.target.value })} />
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">Sfondo della landing</div>
          </div>
          <div>
            <label className="mgx-label">Colore testo</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="w-12 h-10 border border-[#E5E7EB] cursor-pointer" data-testid="hotel-text-color" />
              <input className="mgx-input flex-1" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} />
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">Testi e paragrafi</div>
          </div>
          <div>
            <label className="mgx-label">Font</label>
            <select className="mgx-input bg-white" value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })} data-testid="hotel-font-select">
              {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="mgx-label">Commissione %</label>
            <input type="number" min="0" max="100" step="0.5" className="mgx-input" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} data-testid="hotel-commission-input" />
          </div>
          <div>
            <label className="mgx-label">Codice coupon hotel</label>
            <input
              className="mgx-input font-mono uppercase"
              placeholder="HILTON-PRATO-15"
              value={form.coupon_code}
              onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase().replace(/\s+/g, "") })}
              data-testid="hotel-coupon-code-input"
            />
            <div className="text-[10px] text-[#6B7280] mt-1">Codice unico condiviso con tutti gli ospiti dell'hotel.</div>
          </div>
          <div className="md:col-span-2">
            <label className="mgx-label">Etichetta sconto (mostrata sulla landing)</label>
            <input
              className="mgx-input"
              placeholder="-15% sui materassi Magniflex"
              value={form.coupon_discount_label}
              onChange={(e) => setForm({ ...form, coupon_discount_label: e.target.value })}
              data-testid="hotel-coupon-label-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mgx-label mb-2 block">Prodotti attivi per l'hotel</label>
            {products.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-2">Crea prima dei prodotti nella tab "Prodotti".</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {products.map((p) => {
                  const active = form.active_product_ids.includes(p.id);
                  return (
                    <button type="button" key={p.id} onClick={() => toggleProduct(p.id)}
                      className={`text-left px-4 py-3 border text-sm transition-colors ${active ? "border-[#003D5C] bg-[#003D5C] text-white" : "border-[#E5E7EB] text-[#1A1C20] hover:border-[#003D5C]"}`}
                      data-testid={`hotel-product-toggle-${p.id}`}>
                      {p.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {form.active_product_ids.length > 0 ? (
            <div className="md:col-span-2">
              <label className="mgx-label">Prodotto in vetrina (showcase landing)</label>
              <select
                value={form.featured_product_id}
                onChange={(e) => setForm({ ...form, featured_product_id: e.target.value })}
                className="mgx-input bg-white mt-1"
                data-testid="hotel-featured-product-select"
              >
                <option value="">Auto (il primo prodotto attivo)</option>
                {products.filter((p) => form.active_product_ids.includes(p.id)).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <div className="text-[10px] text-[#6B7280] mt-1">Il prodotto mostrato nella sezione di showcase dopo il feedback.</div>
            </div>
          ) : products.length > 0 ? (
            <div className="md:col-span-2 text-[11px] text-[#6B7280] italic">
              Seleziona prima almeno un prodotto attivo per scegliere quello da mettere in vetrina.
            </div>
          ) : null}
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={loading} className="mgx-btn-primary" data-testid="hotel-save-btn">
              {editing ? "Salva modifiche" : "Crea hotel"}
            </button>
            <button type="button" onClick={reset} className="mgx-btn-secondary">Annulla</button>
          </div>
        </form>
      )}

      <div className="mgx-card">
        <table className="w-full text-sm" data-testid="hotels-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.15em] text-[#6B7280]">
              <th className="py-3 pr-4">Hotel</th>
              <th className="py-3 pr-4">Città</th>
              <th className="py-3 pr-4">Landing URL</th>
              <th className="py-3 pr-4 text-right">Commiss.</th>
              <th className="py-3 pr-4 text-right">Coupon inviati</th>
              <th className="py-3 pr-4">Portale</th>
              <th className="py-3 pr-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {hotels.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-[#6B7280]">Nessun hotel. Creane uno.</td></tr>
            )}
            {hotels.map((h) => {
              const url = `/${h.slug}/${h.city_slug}`;
              return (
                <tr key={h.id} className="border-b border-[#E5E7EB]/50 align-middle">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-8" style={{ background: h.primary_color }}></span>
                      <span className="mgx-display text-lg text-[#003D5C]">{h.name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-[#6B7280]">{h.city}</td>
                  <td className="py-3 pr-4">
                    <a href={url} target="_blank" rel="noreferrer" className="font-mono text-xs text-[#003D5C] hover:text-[#E30613] inline-flex items-center gap-1" data-testid={`hotel-landing-link-${h.id}`}>
                      {url} <ExternalLink size={11} />
                    </a>
                  </td>
                  <td className="py-3 pr-4 text-right">{h.commission_pct}%</td>
                  <td className="py-3 pr-4 text-right">{h.coupons_sent || 0}</td>
                  <td className="py-3 pr-4 text-xs text-[#6B7280]">{h.portal_user_email || <span className="italic">non configurato</span>}</td>
                  <td className="py-3 pr-4 text-right whitespace-nowrap">
                    <button onClick={() => openCoupons(h)} className="text-[#003D5C] hover:text-[#E30613] mr-3" title="Coupon" data-testid={`hotel-coupons-${h.id}`}>
                      <Upload size={14} />
                    </button>
                    <button onClick={() => genCreds(h)} className="text-[#003D5C] hover:text-[#E30613] mr-3" title="Credenziali" data-testid={`hotel-creds-${h.id}`}>
                      <Key size={14} />
                    </button>
                    <button onClick={() => edit(h)} className="text-[#003D5C] hover:text-[#E30613] mr-3" data-testid={`hotel-edit-${h.id}`}>
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => del(h.id)} className="text-[#E30613] hover:opacity-70" data-testid={`hotel-delete-${h.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Credentials Modal */}
      {credsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCredsModal(null)}>
          <div className="bg-white max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="creds-modal">
            <h3 className="mgx-display text-2xl text-[#003D5C]">Credenziali Portale Hotel</h3>
            <p className="text-sm text-[#6B7280] mt-2">Condividi queste credenziali con lo staff di <strong>{credsModal.hotel.name}</strong>.</p>
            <div className="mt-6 space-y-4">
              <div>
                <div className="mgx-label">Email</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-[#F5F6F7] px-3 py-2 font-mono text-sm">{credsModal.email}</code>
                  <button onClick={() => copy(credsModal.email)} className="text-[#003D5C] hover:text-[#E30613]"><Copy size={16} /></button>
                </div>
              </div>
              <div>
                <div className="mgx-label">Password</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-[#F5F6F7] px-3 py-2 font-mono text-sm">{credsModal.password}</code>
                  <button onClick={() => copy(credsModal.password)} className="text-[#003D5C] hover:text-[#E30613]"><Copy size={16} /></button>
                </div>
              </div>
              <div className="border border-[#E30613]/40 bg-[#E30613]/5 p-3 text-xs text-[#1A1C20]">
                <strong>Importante:</strong> la password non sarà più visibile dopo la chiusura. Salvala ora.
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={sendCredsEmail} className="mgx-btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="send-creds-email-btn">
                <Mail size={14} /> Invia via email
              </button>
              <button onClick={() => setCredsModal(null)} className="mgx-btn-secondary flex-1">Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Upload Modal */}
      {couponHotel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCouponHotel(null)}>
          <div className="bg-white max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="coupons-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="mgx-display text-2xl text-[#003D5C]">Coupon — {couponHotel.name}</h3>
                <p className="text-sm text-[#6B7280] mt-2">Carica un file CSV/Excel con i codici coupon (un codice per riga).</p>
              </div>
              <button onClick={() => setCouponHotel(null)} className="text-[#6B7280]"><X size={20} /></button>
            </div>

            {couponStats && (
              <div className="grid grid-cols-4 gap-3 mt-6">
                <div className="border border-[#E5E7EB] p-3"><div className="mgx-label">Totale</div><div className="mgx-display text-2xl text-[#003D5C]">{couponStats.total}</div></div>
                <div className="border border-[#E5E7EB] p-3"><div className="mgx-label">Disponibili</div><div className="mgx-display text-2xl text-[#2E6F40]">{couponStats.available}</div></div>
                <div className="border border-[#E5E7EB] p-3"><div className="mgx-label">Inviati</div><div className="mgx-display text-2xl text-[#E30613]">{couponStats.sent}</div></div>
                <div className="border border-[#E5E7EB] p-3"><div className="mgx-label">Match</div><div className="mgx-display text-2xl text-[#003D5C]">{couponStats.matched}</div></div>
              </div>
            )}

            <div className="border border-dashed border-[#E5E7EB] p-6 mt-6 text-center">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setCouponFile(e.target.files[0])} className="mb-3" data-testid="coupon-file-input" />
              <div className="text-xs text-[#6B7280] mb-3">{couponFile ? couponFile.name : "Nessun file selezionato"}</div>
              <button onClick={uploadCoupons} disabled={!couponFile} className="mgx-btn-primary inline-flex items-center gap-2" data-testid="coupon-upload-btn">
                <Upload size={14} /> Carica codici
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
